import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import { validateLoggedInRoutes } from '../helper/routeHelpers';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import AnalyticsHelper from '../helper/AnalyticsHelper';
import {
  loadInboxPlatformService,
  loadPlatformPolicy,
} from 'dashboard/helper/platformPolicy';
import {
  isPlatformServiceEnabled,
  platformServiceForRoute,
} from 'dashboard/helper/platformPolicyRules';

const ONBOARDING_STEPS = ['account_details', 'enrichment', 'inbox_setup'];
const routes = [...dashboard.routes];

const onboardingPath = step =>
  step === 'inbox_setup' ? 'onboarding/inbox-setup' : 'onboarding';

export const router = createRouter({ history: createWebHistory(), routes });

const shouldCheckPlatformPolicy = route =>
  Boolean(
    platformServiceForRoute(route) || route.path?.includes('/settings')
  );

const platformRouteFallback = (accountId, serviceCode) => {
  const settingsPath =
    serviceCode === 'whatsapp_free'
      ? 'settings/inboxes/list'
      : 'settings/integrations';
  return frontendURL(`accounts/${accountId}/${settingsPath}`);
};

const resolvePlatformService = async (route, accountId, policy) => {
  const declaredService = platformServiceForRoute(route);
  if (
    declaredService ||
    isPlatformServiceEnabled(policy, 'whatsapp_free')
  ) {
    return declaredService;
  }
  return loadInboxPlatformService(route, accountId);
};

const guardPlatformRoute = async (route, accountId, next) => {
  if (!shouldCheckPlatformPolicy(route)) return false;

  const policy = await loadPlatformPolicy(accountId);
  const serviceCode = await resolvePlatformService(route, accountId, policy);
  if (serviceCode === null) {
    next({
      path: platformRouteFallback(accountId, 'whatsapp_free'),
      replace: true,
    });
    return true;
  }
  if (!serviceCode || isPlatformServiceEnabled(policy, serviceCode)) {
    return false;
  }

  next({ path: platformRouteFallback(accountId, serviceCode), replace: true });
  return true;
};

export const validateAuthenticateRoutePermission = async (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    window.location.assign('/app/login');
    return '';
  }

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  const routeAccountId = Number(to.params?.accountId || accountId);
  const userAccount = accounts.find(a => a.id === routeAccountId);
  const isAdmin = userAccount?.role === 'administrator';
  const isActive = userAccount?.status === 'active';
  const needsOnboarding =
    ONBOARDING_STEPS.includes(userAccount?.onboarding_step) &&
    isAdmin &&
    isActive;

  if (userAccount && (await guardPlatformRoute(to, routeAccountId, next))) {
    return '';
  }

  if (to.name === 'no_accounts' || !to.name) {
    const target = needsOnboarding
      ? onboardingPath(userAccount?.onboarding_step)
      : 'dashboard';
    return next(frontendURL(`accounts/${routeAccountId}/${target}`));
  }

  if (needsOnboarding && !isOnOnboardingView(to)) {
    return next(
      frontendURL(
        `accounts/${routeAccountId}/${onboardingPath(userAccount?.onboarding_step)}`
      )
    );
  }
  if (!needsOnboarding && isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/dashboard`));
  }

  const nextRoute = validateLoggedInRoutes(to, store.getters.getCurrentUser);
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = () => {
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach(async (to, _from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    await userAuthentication;
    await validateAuthenticateRoutePermission(to, next, store);
  });
};

export default router;
